import {
  createFeatureSelector,
  createReducer,
  createSelector,
  on,
} from '@ngrx/store';
import {
  assetsDownloaded,
  checkUpdateFailure,
  clearNextSyncToken,
  ContentfulSyncStatus,
  IContentfulState,
  nextSyncTokenUpdate,
  retry,
  syncAssetsFailure,
  syncAssetsSuccess,
  syncEntries,
  syncEntriesFailure,
  syncEntriesSuccess,
  updateAssetsCached,
  updateAssetsDownloaded,
  updateAssetsToCache,
  updateEntriesCached,
  updateEntriesToCache,
} from '@qld-recreational/contentful-library';

export const contentfulFeatureKey = 'contentful';

export const initialState: IContentfulState = {
  error: undefined,
  nextSyncToken: undefined,
  viewStatus: ContentfulSyncStatus.Initial,
  entriesToCache: 0,
  entriesCached: 0,
  assetsToCache: 0,
  assetsDownloaded: 0,
  assetsCached: 0,
};

export const reducer = createReducer(
  initialState,
  on(retry, (state) => ({
    ...state,
    viewStatus: ContentfulSyncStatus.Initial,
    entriesToCache: 0,
    entriesCached: 0,
    assetsToCache: 0,
    assetsDownloaded: 0,
    assetsCached: 0,
  })),
  on(syncEntries, (state) => ({
    ...state,
    viewStatus: ContentfulSyncStatus.UpdateChecked,
  })),
  on(syncEntriesSuccess, (state) => ({
    ...state,
    viewStatus: ContentfulSyncStatus.EntriesSynced,
  })),
  on(assetsDownloaded, (state) => ({
    ...state,
    viewStatus: ContentfulSyncStatus.AssetsDownloaded,
  })),
  on(syncAssetsSuccess, (state) => ({
    ...state,
    viewStatus: ContentfulSyncStatus.AssetsSynced,
  })),
  on(
    syncAssetsFailure,
    syncEntriesFailure,
    checkUpdateFailure,
    (state, action) => ({
      ...state,
      error: action.error,
      viewStatus: ContentfulSyncStatus.Failure,
    }),
  ),
  on(updateEntriesToCache, (state, action) => ({
    ...state,
    entriesToCache: action.entriesToCache,
  })),
  on(updateEntriesCached, (state) => ({
    ...state,
    entriesCached: state.entriesCached + 1,
  })),
  on(updateAssetsToCache, (state, action) => ({
    ...state,
    assetsToCache: action.assetsToCache,
  })),
  on(updateAssetsDownloaded, (state) => ({
    ...state,
    assetsDownloaded: state.assetsDownloaded + 1,
  })),
  on(updateAssetsCached, (state) => ({
    ...state,
    assetsCached: state.assetsCached + 1,
  })),
  on(nextSyncTokenUpdate, (state, action) => ({
    ...state,
    nextSyncToken: action.nextSyncToken,
  })),
  on(clearNextSyncToken, (state) => ({ ...state, nextSyncToken: undefined })),
);

export const selectContentfulState =
  createFeatureSelector<IContentfulState>(contentfulFeatureKey);

export const selectError = createSelector(
  selectContentfulState,
  (state: IContentfulState) => state.error,
);

export const selectViewStatus = createSelector(
  selectContentfulState,
  (state: IContentfulState) => state.viewStatus,
);

export const selectEntriesToDownload = createSelector(
  selectContentfulState,
  (state: IContentfulState) => state.entriesToCache,
);

export const selectEntriesDownloaded = createSelector(
  selectContentfulState,
  (state: IContentfulState) => state.entriesCached,
);

export const selectAssetsToDownload = createSelector(
  selectContentfulState,
  (state: IContentfulState) => state.assetsToCache,
);

export const selectAssetsDownloaded = createSelector(
  selectContentfulState,
  (state: IContentfulState) => state.assetsDownloaded,
);

export const selectAssetsCached = createSelector(
  selectContentfulState,
  (state: IContentfulState) => state.assetsCached,
);

export const selectNextSyncToken = createSelector(
  selectContentfulState,
  (state: IContentfulState) => state.nextSyncToken,
);
